import { CameraAnimationProps } from "@/components/r3f/animations/camera-animation";
import {
  computeLookAtQuaternion,
  computeOverviewCameraPosition,
  useCurrentModelBoundingBox,
  useOverviewTarget,
  usePanosBoundingBox,
} from "@/modes/overview-mode/use-update-overview-camera";
import {
  compute3DWalkAnimationTargetPosition,
  selectPanoPosition,
  toHorizontalFocalAxis,
} from "@/modes/walk-mode/animations/to-3d-walk-animation";
import { CadModelObject } from "@/object-cache";
import {
  isCadModelObject,
  isPointCloudObject,
  UnknownObject,
} from "@/object-cache-type-guard";
import { selectModeName } from "@/store/mode-selectors";
import { useAppSelector } from "@/store/store-hooks";
import { getSheetDiagonal } from "@faro-lotv/app-component-toolbox";
import {
  IElementGenericImgSheet,
  IElementImg360,
} from "@faro-lotv/ielement-types";
import {
  CachedWorldTransform,
  selectIElementWorldTransform,
} from "@faro-lotv/project-source";
import { useMemo } from "react";
import {
  Box3,
  OrthographicCamera,
  Vector3,
  Vector3Tuple,
  type Camera,
} from "three";
import type { AnimationComputer } from "./flywalk-animation";

type InitialAnimationParams = {
  /** Initial camera target */
  target: Vector3 | undefined;
  /** Object to compute the initial camera animation */
  animationComputer: AnimationComputer;
};

/**
 * Computes suitable values for the initial rotation pivot
 * and the initial camera animation when the flywalk mode
 * is entered the first time.
 *
 * @param mainObject current main object in the scene
 * @param overlayObject current overlay object in the scene
 * @param panos current panoramic images in the scene
 * @param sheet current reference sheet for navigation
 * @returns initial animation parameters including target and target camera
 */
export function useInitialAnimationParams(
  mainObject: UnknownObject | null,
  overlayObject: CadModelObject | null,
  panos: IElementImg360[],
  sheet: IElementGenericImgSheet | undefined,
): InitialAnimationParams {
  // Compute the current scene volume information and the target pivot and camera
  const sheetTransform = useAppSelector(
    selectIElementWorldTransform(sheet?.id),
  );
  const pointCloud = isPointCloudObject(mainObject) ? mainObject : null;
  const cad =
    overlayObject ?? (isCadModelObject(mainObject) ? mainObject : null);
  const model3dBox = useCurrentModelBoundingBox(pointCloud, cad);

  const panoBox = usePanosBoundingBox(panos, sheet);

  const modelBox = panoBox ?? model3dBox;
  const target = useOverviewTarget(
    modelBox,
    sheet,
    sheetTransform,
    false,
    undefined,
  );

  const isWalkMode = useAppSelector(selectModeName) === "walk";

  const panoPositions = useAppSelector(selectPanoPosition(panos, sheet));

  const sheetDiagonal = useMemo(
    () => (sheet ? getSheetDiagonal(sheet, sheetTransform.scale) : undefined),
    [sheet, sheetTransform],
  );

  const animationComputer = useMemo(
    () =>
      new InitialAnimationComputer(
        isWalkMode,
        modelBox,
        sheetTransform,
        panoPositions,
        target,
        sheetDiagonal,
      ),
    [
      isWalkMode,
      modelBox,
      sheetTransform,
      panoPositions,
      target,
      sheetDiagonal,
    ],
  );

  return {
    target: isWalkMode ? undefined : target,
    animationComputer,
  };
}

/**
 * This class computes the parameters for the initial to-model animation.
 * This animation should be different if we want to position the camera
 * for an overview of the model or for walking on it. This class stores all
 * parameters needed to compute this custom animation, exactly once when it's needed.
 */
class InitialAnimationComputer implements AnimationComputer {
  /**
   *
   * @param walkAnimation Whether the animation to compute should be suitable to walk around
   * @param modelBox Bounding box of cloud, CAD and panos
   * @param sheetTransform Transform of reference sheet
   * @param panoPositions List of positions of all panos in the scene
   * @param target Initial camera target
   * @param sheetDiagonal Diagonal of hte reference sheet
   */
  constructor(
    private walkAnimation: boolean,
    private modelBox: Box3 | undefined,
    private sheetTransform: CachedWorldTransform,
    private panoPositions: Vector3Tuple[],
    private target: Vector3 | undefined,
    private sheetDiagonal: number | undefined,
  ) {}

  /**
   * @param camera The camera that is currently rendering the scene
   * @returns A suitable animation to enter flywalk mode when a model is active
   */
  compute(camera: Camera): CameraAnimationProps | undefined {
    if (this.walkAnimation) {
      return this.computeWalk(camera);
    }
    return this.computeOverview(camera);
  }

  private computeWalk(camera: Camera): CameraAnimationProps {
    const targetPosition = compute3DWalkAnimationTargetPosition(
      this.panoPositions,
      this.sheetTransform.position[1],
      camera.position.x,
      camera.position.y,
      camera.position.z,
    );
    const targetCameraQuaternion = toHorizontalFocalAxis(camera);
    return {
      position: targetPosition,
      quaternion: targetCameraQuaternion,
    };
  }

  private computeOverview(camera: Camera): CameraAnimationProps | undefined {
    let distance = 1;
    if (this.modelBox) {
      distance = this.modelBox.max.distanceTo(this.modelBox.min) / 2;
    } else if (this.sheetDiagonal) {
      distance = this.sheetDiagonal / 2;
    }

    if (!this.target) return;

    const position = computeOverviewCameraPosition(this.target, distance);

    const quaternion = computeLookAtQuaternion(position, this.target);

    const orthoAspectRatio =
      camera instanceof OrthographicCamera
        ? camera.right / camera.top
        : undefined;

    return {
      position,
      quaternion,
      frustum: orthoAspectRatio
        ? {
            top: distance,
            right: distance * orthoAspectRatio,
            bottom: -distance,
            left: -distance * orthoAspectRatio,
            bboxCenter: this.target,
          }
        : undefined,
    };
  }
}
